/**
 * The maps inside a kingdom, drawn on the same squares the continent is
 * (docs/rpg.md §22).
 *
 * Three scales, each one the inside of a place on the one above it:
 *
 * ```
 *   Overworld  the continent    landmarks are kingdoms      overworld.ts
 *   Town       a kingdom        places are regions          this file
 *   RegionMap  a region         places are concepts         this file
 * ```
 *
 * The overworld answers "how far is the calculus from the arithmetic". A town
 * answers "what is in this kingdom, and where do I go first" — a question five
 * identical region cards answer truthfully and completely flatly. And a region
 * map answers the same question one turn further in, where the honest flat
 * answer is a list of three concept links: the Sum Cottage really is a cottage,
 * with a hearth and a table and three things to learn in it, and the Times
 * Table Grove really is trees planted in a rectangle.
 *
 * **Everything here is scenery over the concept graph, never a second copy of
 * it.** A place holds a *reference* — a region id, a concept id, a boss id —
 * and no name, no flavour text and no progress of its own; the kingdom and the
 * curriculum already own all three, and a plate that restated them could drift
 * out of step with the cards below it. The only exceptions are the two kinds
 * that stand for nothing in the graph (the notice board and the way out), which
 * have nowhere else to take a caption from.
 *
 * **Walking is not an access control** — the invariant this whole package is
 * written to (`docs/rpg-implementation-plan.md` §0.1), and the smaller the map
 * the easier it is to break: a pond can fence off a region, and a *wall* can
 * fence off a concept in a room four tiles wide. `validateTown` and
 * `validateRegionMap` prove on every `npm run content:validate` that every
 * place can be walked to from where the student arrives. Both proofs are a belt
 * over braces anyway: every region and every concept on these maps is an
 * ordinary link on the kingdom page, whether the maps exist or not.
 *
 * The walking itself is `overworld.ts` — `stepFrom`, `pathBetween` and
 * `walkingDistances` all take a `TileGrid` and every map here is one. There is
 * no second search, no clock and no randomness, for the same reasons there are
 * none there.
 */

import {
  positionKey,
  walkingDistances,
  type Position,
  type TileGrid,
} from './overworld.ts';

/**
 * What a place on one of these maps stands for.
 *
 * Five kinds and no sixth without a reason: every one of them is somewhere the
 * kingdom page already goes, which is what keeps a map a *presentation* of that
 * page rather than a route only the map knows about.
 */
export type PlaceKind = 'region' | 'concept' | 'boss' | 'quests' | 'road';

export type Place =
  /** A region of this kingdom. Its name, emoji and progress come from the region. */
  | { kind: 'region'; regionId: string; x: number; y: number }
  /** A concept of this region. Its title and mastery come from the curriculum. */
  | { kind: 'concept'; conceptId: string; x: number; y: number }
  /** The kingdom's boss, wherever the story puts it — a gate, a lair, a tower. */
  | { kind: 'boss'; bossId: string; x: number; y: number }
  /** The quest board. Nothing in the graph names it, so it carries its own words. */
  | { kind: 'quests'; name: string; emoji: string; x: number; y: number }
  /**
   * The way out, one scale up: off a town map to the continent, off a region
   * map back to the town. Which of the two it means is read off the map it
   * stands on rather than authored, because there is only ever one answer and a
   * field for it would be a field that could be wrong.
   */
  | { kind: 'road'; name: string; emoji: string; x: number; y: number };

/** What a town and a region map have in common: ground, an arrival tile, signposts. */
export type PlaceMap = TileGrid & {
  /** One line under the map's heading, in the place's own voice. */
  caption: string;
  /** Where a student arriving here is put down. */
  start: Position;
  places: readonly Place[];
};

/** A kingdom, at village scale. Places are its regions, its boss and its board. */
export type Town = PlaceMap & { kingdomId: string };

/** A region, at room-and-field scale. Places are the concepts in it. */
export type RegionMap = PlaceMap & { kingdomId: string; regionId: string };

/**
 * A place's identity, for a React key and for a test that wants to name one.
 *
 * Derived rather than authored: a hand-written id would be a fourth name for a
 * region that already has three, and the pair (kind, what it points at) is
 * unique by the rule the validators enforce anyway.
 */
export const placeKey = (place: Place): string =>
  place.kind === 'region'
    ? `region:${place.regionId}`
    : place.kind === 'concept'
      ? `concept:${place.conceptId}`
      : place.kind === 'boss'
        ? `boss:${place.bossId}`
        : `${place.kind}:${place.name}`;

export const placeAt = (map: PlaceMap, position: Position): Place | undefined =>
  map.places.find((place) => place.x === position.x && place.y === position.y);

export const regionPlace = (map: PlaceMap, regionId: string): Place | undefined =>
  map.places.find((place) => place.kind === 'region' && place.regionId === regionId);

export const conceptPlace = (map: PlaceMap, conceptId: string): Place | undefined =>
  map.places.find((place) => place.kind === 'concept' && place.conceptId === conceptId);

/** The town for a kingdom, or nothing — a kingdom without one is drawn in cards alone. */
export const townOf = (towns: readonly Town[], kingdomId: string | undefined): Town | undefined =>
  kingdomId === undefined ? undefined : towns.find((town) => town.kingdomId === kingdomId);

/** The map of one region, or nothing — a region without one is a card, as they all were. */
export const regionMapOf = (maps: readonly RegionMap[], regionId: string | undefined): RegionMap | undefined =>
  regionId === undefined ? undefined : maps.find((map) => map.regionId === regionId);

/**
 * The nearest place within `radius` tiles, by walking distance rather than by
 * how it looks — the far side of the mill stream is not nearby, and neither is
 * the next room.
 *
 * This is what the live region under a map announces, so a student who cannot
 * see the tiles still hears "Sum Cottage is three steps south-west". The
 * default radius is tighter than the continent's because a village is smaller;
 * a region map, smaller still, passes its own.
 *
 * `only` narrows what counts. The caller uses it for one thing: every one of
 * these maps is entered through its own door, so the door is the nearest place
 * to the tile a student arrives on, and "The Garden Path is 1 step south" is a
 * welcome that tells them to leave. Asked twice — once for somewhere to go, and
 * again for anything at all — the sentence names the way out only when there is
 * genuinely nothing else nearby.
 */
export function nearestPlace(
  map: PlaceMap,
  from: Position,
  radius = 5,
  only?: (place: Place) => boolean,
): { place: Place; steps: number } | undefined {
  const distances = walkingDistances(map, from, radius);
  let best: { place: Place; steps: number } | undefined;
  for (const place of map.places) {
    if (only && !only(place)) continue;
    const steps = distances.get(positionKey(place));
    if (steps === undefined) continue;
    if (!best || steps < best.steps) best = { place, steps };
  }
  return best;
}
